import { useStore } from '@nanostores/react';
import type { Message } from 'ai';
import { useChat } from '@ai-sdk/react';
import { useAnimate } from 'framer-motion';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { cssTransition, toast, ToastContainer } from 'react-toastify';
import { useMessageParser, usePromptEnhancer, useShortcuts, useSnapScroll } from '~/lib/hooks';
import { chatId, db, useChatHistory } from '~/lib/persistence';
import { chatStore } from '~/lib/stores/chat';
import { selectionStore, type Framework, type Language } from '~/lib/persistence/selectionStore';
import { workbenchStore } from '~/lib/stores/workbench';
import { fileModificationsToHTML } from '~/utils/diff';
import { cubicEasingFn } from '~/utils/easings';
import { createScopedLogger, renderLogger } from '~/utils/logger';
import { BaseChat } from './BaseChat.client';
import { getGithubCode, getTemplates } from '~/utils/getTemplates';
import { Figma_Meta_Data, API_SPEC_TAG_NAME, API_SPEC_METHOD_NAME } from '~/utils/constants';
import type { StoreFileAction } from '~/types/actions';
import type { ActionCallbackData } from '~/lib/runtime/message-parser';
import { ErrorModal } from '~/components/ui/ErrorModal';
import { MigrationModal } from '~/components/ui/MigrationModal';
import { json, useLoaderData, useNavigate } from '@remix-run/react';
import { useMigrationParser } from '~/lib/hooks/useMigrationParser';
import { migrateChatHistory } from '~/lib/persistence/migrateChatHistory';
import { checkBoltHistoryExists, getMessagesById, updateFramework } from '~/lib/persistence/db';
import { ProjectRulesModal } from './ProjectRulesModal';
import { rulesStore, loadChatSpecificRules } from '~/lib/stores/rules';
import { PROJECT_RULES_TAG_NAME } from '~/utils/constants';
import { extractFrameworks } from '~/utils/extractFrameworks';
import GithubModal from './GithubModal';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';
import type { LoaderFunction } from '@remix-run/cloudflare';

type AuthFrontendEnvs = {
  GITHUB_CLIENT_ID: string;
  GITHUB_CLIENT_SECRET: string;
};

// Loader function to expose environment variables
export const loader: LoaderFunction = async ({ context }) => {
  const cloudflareEnv = context.cloudflare.env;

  const env: AuthFrontendEnvs = {
    GITHUB_CLIENT_ID: process.env.GITHUB_CLIENT_ID || cloudflareEnv.GITHUB_CLIENT_ID,
    GITHUB_CLIENT_SECRET: process.env.GITHUB_CLIENT_SECRET || cloudflareEnv.GITHUB_CLIENT_SECRET,
  };

  return json(env);
};

const toastAnimation = cssTransition({
  enter: 'animated fadeInRight',
  exit: 'animated fadeOutRight',
});

const { email } = userStore.get();

const logger = createScopedLogger('Chat');

export function Chat() {
  renderLogger.trace('Chat');

  const navigate = useNavigate();

  const { ready, initialMessages, initialFileActions, storeMessageHistory, storeFileActions, getFileActions } =
    useChatHistory();
  const { parseMessagesForMigration } = useMigrationParser();
  const [migrationComplete, setMigrationComplete] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [needsMigration, setNeedsMigration] = useState(false);

  // Check if migration is needed
  useEffect(() => {
    async function checkMigrationNeeded() {
      try {
        // Check if migration was already completed using migration flag
        const alreadyMigrated = localStorage.getItem('alpha-migrated-to-v2') === 'true';

        if (!alreadyMigrated) {
          // Check if boltHistory exists
          const boltHistoryExists = await checkBoltHistoryExists();

          if (boltHistoryExists) {
            console.log('[Chat] Migration needed');
            setNeedsMigration(true);
            setShowMigrationModal(true);
          } else {
            // No migration needed, mark as completed
            localStorage.setItem('alpha-migrated-to-v2', 'true');
            setMigrationComplete(true);
          }
        } else {
          // Already migrated
          setMigrationComplete(true);
        }
      } catch (error) {
        alert('Migration check failed. Please try again later: ' + email);
        Sentry.captureException(error, {
          extra: {
            message: 'Error checking migration status',
          },
          level: 'warning',
        });

        logger.error('Migration check failed:', error);

        // TODO: Discuss with @prakhar

        // Continue anyway

        // setMigrationComplete(true);
      }
    }

    async function setTechStack() {
      if (db && chatId.get()) {
        const chat = await getMessagesById(db, chatId.get()!);
        const techStack = chat?.techStack;
        console.log('techStack from Chat.client.tsx useEffect >>>', techStack);

        if (techStack) {
          // Only set if values are not "unknown"
          if (techStack.framework !== 'unknown') {
            selectionStore.setKey('framework', techStack.framework as Framework);
          }

          if (techStack.language !== 'unknown') {
            selectionStore.setKey('language', techStack.language as Language);
          }
        } else {
          if (chat && chat.fileActions.length > 0) {
            const techStack = extractFrameworks(chat);
            console.log('techStack from extractFrameworks in Chat.client.tsx useEffect >>>', techStack);

            // Only set if values are not "unknown"
            if (techStack.framework !== 'unknown') {
              selectionStore.setKey('framework', techStack.framework as Framework);
            }

            if (techStack.language !== 'unknown') {
              selectionStore.setKey('language', techStack.language as Language);
            }

            // Only update DB if we have valid values
            if (techStack.framework !== 'unknown' || techStack.language !== 'unknown') {
              updateFramework(db, chatId.get()!, techStack?.framework, techStack?.language);
            }
          }
        }
      }
    }

    if (ready) {
      checkMigrationNeeded();
      setTechStack();
    }
  }, [ready]);

  // Function to run the actual migration
  const runMigration = async (): Promise<boolean> => {
    try {
      logger.info('Starting migration process');

      const success = await migrateChatHistory(parseMessagesForMigration);

      if (success) {
        logger.info('Migration completed successfully');
        localStorage.setItem('alpha-migrated-to-v2', 'true');
      } else {
        logger.error('Migration failed');
      }

      return success;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Migration process failed',
        },
        level: 'error',
      });

      logger.error('Migration failed with error:', error);
      return false;
    }
  };

  // Function to handle migration completion
  const handleMigrationComplete = () => {
    setMigrationComplete(true);
    setShowMigrationModal(false);

    // Navigate to home and reload to refresh the app state
    navigate('/');
    window.location.reload();
  };

  console.log('[refresh] initialFileActions from Chat.client.tsx:', initialFileActions);

  return (
    <>
      {ready && migrationComplete && (
        <ChatImpl
          initialMessages={initialMessages}
          initialFileActions={initialFileActions}
          storeMessageHistory={storeMessageHistory}
          storeFileActions={storeFileActions}
          getFileActions={getFileActions}
        />
      )}

      {/* Migration Modal */}
      <MigrationModal isOpen={showMigrationModal} onContinue={runMigration} onComplete={handleMigrationComplete} />

      <ToastContainer
        closeButton={({ closeToast }) => {
          return (
            <button className="Toastify__close-button" onClick={closeToast}>
              <div className="i-ph:x text-lg" />
            </button>
          );
        }}
        icon={({ type }) => {
          /**
           * @todo Handle more types if we need them. This may require extra color palettes.
           */
          switch (type) {
            case 'success': {
              return <div className="i-ph:check-bold text-alpha-elements-icon-success text-2xl" />;
            }
            case 'error': {
              return <div className="i-ph:warning-circle-bold text-alpha-elements-icon-error text-2xl" />;
            }
          }

          return undefined;
        }}
        position="bottom-right"
        pauseOnFocusLoss
        transition={toastAnimation}
      />
    </>
  );
}

interface ChatProps {
  initialMessages: Message[];
  initialFileActions: StoreFileAction[];
  storeMessageHistory: (messages: Message[], fileActions: StoreFileAction[]) => Promise<void>;
  storeFileActions: (fileActions: StoreFileAction[]) => Promise<void>;
  getFileActions: () => Promise<StoreFileAction[]>;
}

export const ChatImpl = memo(
  ({ initialMessages, initialFileActions, storeMessageHistory, storeFileActions, getFileActions }: ChatProps) => {
    useShortcuts();

    const navigate = useNavigate();
    const previews = useStore(workbenchStore.previews);

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const [chatStarted, setChatStarted] = useState(initialMessages.length > 0);
    const [showTemplateFailModal, setShowTemplateFailModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState<{ message: string; title: string; isTLE?: boolean }>({
      message: 'This is an error message',
      title: 'Error',
      isTLE: false,
    });

    const { showChat } = useStore(chatStore);
    const { framework, language } = useStore(selectionStore);

    const [animationScope, animate] = useAnimate();

    const checkInternetConnection = async (): Promise<boolean> => {
      try {
        return navigator.onLine;
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            message: 'Error checking internet connection',
          },
          level: 'fatal',
        });

        console.error('Connection check error:', error);
        return navigator.onLine;
      }
    };

    console.log('[chat] framework from chatStore:', framework);

    const { messages, status, input, handleInputChange, setInput, stop, append, setMessages, reload } = useChat({
      api: '/api/chat',
      onError: async (error: Error) => {
        Sentry.captureException(error, {
          extra: {
            message: 'error occurred in /api/chat',
          },
          level: 'fatal',
        });

        logger.error('Request failed\n\n', error);

        // Check internet connection first
        const isOnline = await checkInternetConnection();

        if (!isOnline) {
          toast.error(
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <p className="font-medium">No Internet Connection</p>
              </div>
              <div className="mt-1 text-sm text-alpha-elements-textSecondary">
                <p>Please check your internet connection and try again.</p>
              </div>
            </div>,
            {
              autoClose: 10000,
              className: 'error-toast-enhanced',
              position: 'bottom-right',
            },
          );
          return;
        }

        let errorData;

        try {
          errorData = JSON.parse(error.message);
        } catch {
          errorData = { error: error.message };
        }

        // Handle specific error codes
        switch (errorData.code) {
          case 'TOKEN_LIMIT_EXCEEDED': {
            setShowTemplateFailModal(true);
            setErrorMessage({
              message: "You've consumed your free trial, and will need to upgrade",
              title: "Your free trial's over",
              isTLE: true,
            });

            break;
          }
          case 'AUTHORIZATION_FAILED': {
            toast.error('Request authorization failed. \n Make sure you are logged in a active account');
            break;
          }
          case 'INVALID_REQUEST': {
            toast.error('Invalid request. Please try again.');
            break;
          }
          case 'INVALID_OPTIONS': {
            toast.error('Invalid configuration.');
            break;
          }
          case 'SERVER_OVERLOAD': {
            toast.error('LLM Server is currently overloaded. Please try again later.');
            break;
          }
          case 'STREAMING_ERROR': {
            toast.error('Failed to generate response. Please try again.');
            break;
          }
          case 'INTERNAL_ERROR': {
            toast.error('An unexpected error occurred. Please try again later.');
            break;
          }
          default: {
            // Handle legacy error messages
            if (error.message.toLowerCase().includes('rate limit')) {
              toast.error('Rate limit exceeded. Please try again in a few minutes.');
            } else if (error.message.toLowerCase().includes('timeout')) {
              toast.error('Request timed out. Please try again.');
            } else {
              toast.error(
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">Sorry, there was an issue processing your request</p>
                  </div>
                  <div className="mt-1 text-sm text-alpha-elements-textSecondary">
                    <p>Please try:</p>
                    <ul className="mt-2 space-y-1.5 list-inside list-disc">
                      <li className="flex items-center gap-1">
                        <div className="i-ph:arrows-clockwise" />
                        Refreshing the page
                      </li>
                      <li className="flex items-center gap-1">
                        <div className="i-ph:power" />
                        Restarting the app
                      </li>
                    </ul>
                  </div>
                </div>,
                {
                  autoClose: 10000,
                  className: 'error-toast-enhanced',
                  position: 'bottom-right',
                },
              );
            }
          }
        }

        chatStore.setKey('aborted', true);
        chatStore.setKey('error', true);
        chatStore.setKey('isStreaming', false);
      },
      onFinish: () => {
        logger.debug('Finished streaming');
        chatStore.setKey('error', false);
        chatStore.setKey('isStreaming', false);
      },
      initialMessages,
    });

    const { enhancingPrompt, promptEnhanced, enhancePrompt, resetEnhancer } = usePromptEnhancer();
    const { parsedMessages, parseMessages, fileActions } = useMessageParser();

    const TEXTAREA_MAX_HEIGHT = chatStarted ? 400 : 200;

    const [attachedImage, setAttachedImage] = useState<string | null>(null);
    const [extractedDesign, setExtractedDesign] = useState<{
      image: string | null;
      fonts: string[];
      colors: string[];
      imageUrls: string[];
    } | null>(null);

    const hasProcessedInitialMessages = useRef(false);

    const [uiStreaming, setUiStreaming] = useState(false);
    const [promptSubmited, setPromptSubmited] = useState(false);

    const [showRulesModal, setShowRulesModal] = useState(false);
    const { projectRules, loaded } = useStore(rulesStore);

    const [showGithubModal, setShowGithubModal] = useState(false);
    const githubCreds = useLoaderData<AuthFrontendEnvs>();

    // const [githubRepo, setGithubRepo] = useState<string | null>(null);
    let githubRepo = '';
    useEffect(() => {
      chatStore.setKey('started', initialMessages.length > 0);
      localStorage.setItem('flutterCode', '');
      localStorage.setItem('dualite_api_data', '');
      localStorage.setItem('dualite_api_data_yaml', '');
    }, []);

    // Initialize rules store and handle chat changes
    useEffect(() => {
      console.log('[chat] Rules state - chatStarted:', chatStarted, 'chatId:', chatId.get());
      loadChatSpecificRules();
    }, [chatId.get(), chatStarted]);

    useEffect(() => {
      console.log('status from Chat.client.tsx: ', status);
    }, [status]);

    console.log('[Chat] status: ', status);

    useEffect(() => {
      console.log('Messages array in Chat.client.tsx: ', messages);

      // only parse messages once after initial load
      if (!hasProcessedInitialMessages.current && messages.length > 0 && status === 'ready') {
        console.log('Processing initial messages after refresh');

        parseMessages(messages, false, initialMessages.length);
        hasProcessedInitialMessages.current = true;

        if (initialFileActions.length > 0) {
          workbenchStore.addAllActions(initialFileActions, 'import-files-action', 'file-actions', 'File action chat');
        }
      }
      // only parse during streaming or when new messages are added
      else if (
        status !== 'ready' ||
        (hasProcessedInitialMessages.current && messages.length > initialMessages.length)
      ) {
        console.log('Processing during streaming or new messages');
        hasProcessedInitialMessages.current = true;
        parseMessages(messages, status !== 'ready', initialMessages.length);
      }
    }, [messages, status, parseMessages, initialMessages.length]);

    useEffect(() => {
      console.log('[Chat] fileActions: ', fileActions);

      if (messages.length > initialMessages.length) {
        storeMessageHistory(messages, fileActions).catch((error) => toast.error(error.message));
      }
    }, [fileActions, messages, initialMessages.length]);

    const scrollTextArea = () => {
      const textarea = textareaRef.current;

      if (textarea) {
        textarea.scrollTop = textarea.scrollHeight;
      }
    };

    const abort = () => {
      console.log('[Chat] Aborting chat and workbench actions');
      stop();
      setUiStreaming(false);
      setPromptSubmited(false);
      chatStore.setKey('aborted', true);
      workbenchStore.abortAllActions();
    };

    useEffect(() => {
      const textarea = textareaRef.current;

      if (textarea) {
        textarea.style.height = 'auto';

        const scrollHeight = textarea.scrollHeight;

        textarea.style.height = `${Math.min(scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
        textarea.style.overflowY = scrollHeight > TEXTAREA_MAX_HEIGHT ? 'auto' : 'hidden';
      }
    }, [input, textareaRef]);

    const runAnimation = async () => {
      if (chatStarted) {
        return;
      }

      await Promise.all([
        animate('#examples', { opacity: 0, display: 'none' }, { duration: 0.1 }),
        animate('#intro', { opacity: 0, flex: 1 }, { duration: 0.2, ease: cubicEasingFn }),
      ]);

      chatStore.setKey('started', true);

      setChatStarted(true);
    };

    const aiModel = useStore(selectionStore).aiModel;

    const sendMessage = async (_event: React.UIEvent, messageInput?: string) => {
      console.log('[assistant] messages from sendMessage: ', messages);

      const _input = messageInput || input;

      if (_input.length === 0 || status === 'streaming' || status === 'submitted') {
        return;
      }

      if (!(githubRepo && !chatStarted)) {
        setPromptSubmited(true);
      }

      // setUiStreaming(true);

      let designContext = `${extractedDesign?.imageUrls ? `Images used in the design: ${extractedDesign.imageUrls}\n` : ''}${extractedDesign?.fonts ? `Fonts used in the design: ${extractedDesign.fonts}\n` : ''}${extractedDesign?.colors ? `Colors used in the design: ${extractedDesign.colors}\n` : ''}`;

      if (designContext.length > 0) {
        designContext = `Design Context:
      ${designContext}`;
      }

      let openApiSpec = localStorage.getItem('dualite_api_data');
      const openApiSpecMethod = localStorage.getItem('dualite_api_call_method');
      const openApiSpecYaml = localStorage.getItem('dualite_api_data_yaml');
      const token = localStorage.getItem('dualitex-alpha-access-token');

      if (openApiSpec) {
        if (openApiSpec === 'DUALITEX_INFER_YOURSELF') {
          openApiSpec = `Please infer an appropriate API schema based on the UI components.
    Use example.com/api as the base URL for API calls.
    Since the API endpoints won't be available, implement with default state values
    that match the expected API response format.
    `;
          openApiSpec +=
            openApiSpecYaml === 'true'
              ? `Also, please provide the API schema in the OpenAPI format in <api-name>.yaml file in the root directory of the project.`
              : '';
        }
      }

      let content = _input;

      if (designContext.length > 0) {
        content += `\n\n<${Figma_Meta_Data}>\n${designContext}\n</${Figma_Meta_Data}>\n\n`;
      }

      if (projectRules) {
        content += `\n\n<${PROJECT_RULES_TAG_NAME}>\n${projectRules}\n</${PROJECT_RULES_TAG_NAME}>\n\n`;
      }

      if (openApiSpec) {
        content += `\n\n<${API_SPEC_TAG_NAME}>\n${openApiSpec}\n</${API_SPEC_TAG_NAME}>\n\n<${API_SPEC_METHOD_NAME}>\n${openApiSpecMethod}\n</${API_SPEC_METHOD_NAME}>\n\n`;
      }

      console.log('[chat] content: ', content);
      console.log('[chat] previews: ', previews);
      console.log('[chat] current framework:', framework);

      if (!chatStarted) {
        console.log('[chat] chatStarted: ', chatStarted);

        // Special handling for Flutter - skip template fetching
        if (framework === 'flutter') {
          console.log('[chat] Flutter framework detected, skipping template import');

          // Run animation to transition to chat UI
          await runAnimation();

          // Directly send the messages
          append(
            { role: 'user', content },
            { body: { framework, language, attachedImage, token, fileActions, aiModel } },
          );
          setInput('');
          resetEnhancer();
          textareaRef.current?.blur();
          return;
        }

        // setFakeLoading(true);

        try {
          console.log('[chat] Attempting to fetch templates for', framework, language);

          let temResp;
          console.log('[chat] githubRepo: ', githubRepo);

          if (githubRepo) {
            console.log('[chat] Attempting to fetch github code for', framework, language);
            temResp = await getGithubCode(framework, language, githubRepo);
          } else {
            console.log('[chat] Attempting to fetch templates for', framework, language);
            temResp = await getTemplates(framework, language);
          }

          console.log('[chat] temResp: ', temResp);

          if (!temResp) {
            console.error('[chat] Template response is null or undefined');

            // Continue with the chat but show the modal
            await runAnimation();
            setErrorMessage({ message: 'Failed to import starter template.', title: 'Starter Template' });
            setShowTemplateFailModal(true);

            // Stop any ongoing API call
            stop();
            setPromptSubmited(false);
            setUiStreaming(false);

            // Don't append or send the message
            setInput('');
            resetEnhancer();
            textareaRef.current?.blur();
            return;
          }

          // console.log('[chat] Successfully fetched templates');

          // Now that we have the templates, run the animation
          await runAnimation();

          const { assistantMessage, userMessage } = temResp;
          const messages = [
            {
              id: `1-${new Date().getTime()}`,
              role: 'user',
              content,
            },
            {
              id: `2-${new Date().getTime()}`,
              role: 'assistant',
              content: assistantMessage,
            },
            {
              id: `3-${new Date().getTime()}`,
              role: 'user',
              content: userMessage,
            },
          ];
          setMessages(messages as Message[]);

          if (githubRepo) {
            console.log('[chat] Reloading with github repo');
            storeMessageHistory(messages as Message[], fileActions).catch((error) => toast.error(error.message));
          }

          if (!githubRepo) {
            console.log('[chat] Reloading with github repo');
            await Sentry.startSpan(
              {
                name: 'Chat Template Reload',
                op: 'reload.chat',
              },
              async () => {
                reload({ body: { framework, language, attachedImage, token, fileActions, aiModel } });
              },
            );
          }

          console.log('input field data from if', input);
          setInput('');

          resetEnhancer();
          textareaRef.current?.blur();

          return;
        } catch (error: unknown) {
          console.error('[chat] Template fetch error:', error);

          // Continue with the chat but show the modal
          await runAnimation();

          // Set error message for the modal
          let errorMessage = 'Failed to import starter template. Please try again later.';

          if (error instanceof Error) {
            if (error.message.includes('rate limit')) {
              errorMessage = 'Rate limit exceeded. Please wait a moment before trying again.';
            } else if (error.message.includes('GitHub API error')) {
              errorMessage = 'Unable to fetch templates from GitHub. Please check your connection and try again.';
            }
          }

          setErrorMessage({ message: errorMessage, title: 'Template Fetch Error' });
          setShowTemplateFailModal(true);

          // Stop any ongoing API call
          stop();
          setPromptSubmited(false);
          setUiStreaming(false);

          // Don't append or send the message
          setInput('');
          resetEnhancer();
          textareaRef.current?.blur();
          return;
        }
      }

      console.log('[chat] chatStarted: ', chatStarted);

      /**
       * @note (delm) Usually saving files shouldn't take long but it may take longer if there
       * many unsaved files. In that case we need to block user input and show an indicator
       * of some kind so the user is aware that something is happening. But I consider the
       * happy case to be no unsaved files and I would expect users to save their changes
       * before
       *  they send another message.
       */

      /**
       *@note (delm) add back when we need to save the unsaved user changes before the ai call
       *console.log('[refresh] save files...: ', initialFileActions);
       *await workbenchStore.saveAllFiles(initialFileActions, storeFileActions);
       */
      // const fileModifications = workbenchStore.getFileModifcations();

      /*
       * if (fileModifications !== undefined) {
       *   const diff = fileModificationsToHTML(fileModifications);
       *   content = diff + '\n\n' + _input;
       * }
       */

      const latestFileActions = await getFileActions();

      chatStore.setKey('aborted', false);

      console.log('[Chat.client.tsx] appending fileActions: ', fileActions);

      await Sentry.startSpan(
        {
          name: 'Chat API Call',
          op: 'append.chat',
        },
        async () => {
          append(
            { role: 'user', content },
            { body: { framework, language, attachedImage, token, fileActions: latestFileActions, aiModel } },
          );
        },
      );

      setInput('');
      setAttachedImage(null);
      resetEnhancer();

      textareaRef.current?.blur();
    };

    const handleImageAttachment = (imageData: string) => {
      setAttachedImage(imageData);
    };

    const removeAttachedImage = () => {
      setExtractedDesign(null);
      setAttachedImage(null);
    };

    const [messageRef, scrollRef] = useSnapScroll();
    const handleExtractComplete = (data: {
      image: string | null;
      fonts: string[];
      colors: string[];
      imageUrls: string[];
    }) => {
      console.log('[chat] handleExtractComplete: ', data);
      setExtractedDesign(data);

      if (data.image) {
        handleImageAttachment(data.image);
      }
    };

    const isFlutter = selectionStore.get().framework === 'flutter';

    const handleContinueToNewChat = useCallback(() => {
      setShowTemplateFailModal(false);

      // Go back to initial state
      setChatStarted(false);
      chatStore.setKey('started', false);

      setMessages([]);
      setInput('');
      navigate('/');
    }, [navigate]);

    const [uploadingToWorkBench, setUploadingToWorkBench] = useState(false);

    const handleGithubRepoSubmit = async (repoUrl: string) => {
      setUploadingToWorkBench(true);
      console.log('[chat] handleGithubRepoSubmit: ', repoUrl);
      githubRepo = repoUrl;

      console.log('[chat] githubRepo: ', githubRepo);

      const githubPrompt = `Importing code from github`;
      await sendMessage(null as any, githubPrompt);
      setShowGithubModal(false);
      setUploadingToWorkBench(false);
    };

    useEffect(() => {
      if (status === 'streaming') {
        setPromptSubmited(false);
        setUiStreaming(true);
      } else if (status === 'submitted') {
        setUiStreaming(false);
        setPromptSubmited(true);
      } else if (status === 'ready') {
        setUiStreaming(false);
        setPromptSubmited(false);
      } else {
        setUiStreaming(false);
        setPromptSubmited(false);
      }
    }, [status]);

    useEffect(() => {
      // This is the exact logic from your BaseChat prop
      const combinedIsStreaming = uiStreaming || status === 'streaming';

      // Update the store only if the value has changed
      if (chatStore.get().isStreaming !== combinedIsStreaming) {
        chatStore.setKey('isStreaming', combinedIsStreaming);
      }
    }, [uiStreaming, status]); // Re-run when uiStreaming or status changes

    // Listen for error fix events from the preview component
    useEffect(() => {
      const handleErrorFix = (event: CustomEvent<{ prompt: string }>) => {
        if (event.detail && event.detail.prompt) {
          console.log('Received error fix event:', event.detail.prompt);

          // Send the error fix prompt through the chat
          sendMessage(new CustomEvent('error-fix') as unknown as React.UIEvent, event.detail.prompt);
        }
      };

      // Register the event listener
      document.addEventListener('dualite-fix-error', handleErrorFix as EventListener);

      // Clean up the event listener when component unmounts
      return () => {
        document.removeEventListener('dualite-fix-error', handleErrorFix as EventListener);
      };
    }, [sendMessage]);

    return (
      <>
        <BaseChat
          ref={animationScope}
          textareaRef={textareaRef}
          input={input}
          showChat={showChat}
          chatStarted={chatStarted}
          isStreaming={uiStreaming || status === 'streaming'}
          promptSubmited={promptSubmited}
          enhancingPrompt={enhancingPrompt}
          promptEnhanced={promptEnhanced}
          sendMessage={sendMessage}
          messageRef={messageRef}
          scrollRef={scrollRef}
          handleInputChange={handleInputChange}
          handleStop={abort}
          attachedImage={attachedImage}
          onAttachImage={handleImageAttachment}
          onRemoveAttachedImage={removeAttachedImage}
          onExtractComplete={handleExtractComplete}
          messages={messages.map((message, i) => {
            if (message.role === 'user') {
              return message;
            }

            return {
              ...message,
              content: parsedMessages[i] || '',
            };
          })}
          enhancePrompt={() => {
            enhancePrompt(input, (input) => {
              setInput(input);
              scrollTextArea();
            });
          }}
          isFlutter={isFlutter}
          extractedDesign={extractedDesign}
          projectRules={projectRules}
          onEditProjectRules={() => setShowRulesModal(true)}
          setGithubModalIsOpen={setShowGithubModal}
        />
        <ErrorModal
          isOpen={showTemplateFailModal}
          onClose={() => setShowTemplateFailModal(false)}
          onRetry={handleContinueToNewChat}
          title={`${errorMessage.title}`}
          message={`${errorMessage.message}`}
          isTLE={errorMessage.isTLE}
        />
        <ProjectRulesModal isOpen={showRulesModal} onClose={() => setShowRulesModal(false)} />
        <GithubModal
          isOpen={showGithubModal}
          onClose={() => setShowGithubModal(false)}
          onSubmitRepo={handleGithubRepoSubmit}
          isUploadingToWb={uploadingToWorkBench}
          githubCreds={githubCreds}
        />
      </>
    );
  },
);
